import React from 'react';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import {
  LogIn,
  LogOut,
  Coffee,
  Play,
  Pencil,
  Trash2,
  AlertTriangle,
  MapPinOff,
  Clock,
  PlayCircle,
  StopCircle,
  UserX,
  Calendar,
  CalendarOff,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import type { TimeTrackingActivityEventWithEmployee } from '@/types/time-tracking-activity';
import { ACTIVITY_EVENT_CONFIG } from '@/types/time-tracking-activity';
import { getActivityEventDescription } from '@/hooks/attendance/useTimeTrackingActivityLog';

interface CompactActivityLogRowProps {
  event: TimeTrackingActivityEventWithEmployee;
  isSelected?: boolean;
  isNew?: boolean;
  onClick?: () => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  LogIn,
  LogOut,
  Coffee,
  Play,
  Pencil,
  Trash2,
  AlertTriangle,
  MapPinOff,
  Clock,
  PlayCircle,
  StopCircle,
  UserX,
  Calendar,
  CalendarOff,
};

export const CompactActivityLogRow: React.FC<CompactActivityLogRowProps> = ({
  event,
  isSelected = false,
  isNew = false,
  onClick,
}) => {
  const config = ACTIVITY_EVENT_CONFIG[event.event_type];
  const Icon = ICON_MAP[config.icon] || Clock;
  const description = getActivityEventDescription(event);

  const eventTime = new Date(event.event_timestamp);
  const timeDisplay = format(eventTime, 'h:mm a');

  return (
    <div
      className={cn(
        'flex items-center gap-2 py-1.5 px-2 text-sm cursor-pointer transition-colors',
        'hover:bg-muted/30',
        isSelected && 'border-l-2 border-primary bg-muted/50',
        !isSelected && 'border-l-2 border-transparent',
        isNew && 'bg-primary/5'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Small icon without background */}
      <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', config.color)} />

      {/* Description - truncated */}
      <span className="flex-1 truncate text-foreground/90">{description}</span>

      {/* Time on right */}
      <span className="flex-shrink-0 text-xs text-muted-foreground tabular-nums">
        {timeDisplay}
      </span>
    </div>
  );
};

export default CompactActivityLogRow;
